// Display helpers for the Technical details sheet (DESIGN-LOG §47): how his panel's numbers, dates, sizes, table names
// and client rows read in the V2 editor. Pure functions; his panel calls them in its display code only.
import { formatDay } from "./dates"

// One size format for a file, in the sheet and in the file Preview modal (T7): "89 KB", "29 MB", "512 B".
export function fileSize(bytes: number, intl = "en-GB"): { key: "b" | "kb" | "mb"; size: string } {
  if (bytes < 1024) return { key: "b", size: String(Math.max(0, Math.round(bytes))) }
  if (bytes < 1024 * 1024) return { key: "kb", size: Math.round(bytes / 1024).toLocaleString(intl) }
  const mb = bytes / 1024 / 1024
  return { key: "mb", size: mb.toLocaleString(intl, { maximumFractionDigits: mb < 10 ? 1 : 0 }) }
}

// A count with grouping: 35150 → "35,150".
export function formatCount(value: number, intl = "en-GB") {
  return Number.isFinite(value) ? Math.round(value).toLocaleString(intl) : ""
}

// "Hamad_70714 — top_holdings" → "Top holdings": the client prefix goes, snake case reads as words (the canvas's own
// readableTableName, DatoxComplianceDashboard.jsx).
export function readableTableName(name: string) {
  const table = String(name || "").split(/\s+[—–]\s+/).at(-1)?.trim() ?? ""
  const words = table.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  return words ? words[0].toUpperCase() + words.slice(1) : String(name || "")
}

// One rule for finding a table by its name, wherever a table named by his categories meets the tables an extraction read:
// the Data tab's list and Overview, a table's Rows and History, his Tables cards (code review of bd134fb..1a77a15, #2,
// #7). Names compare as words (case, "_", "—", "·" and other marks aside). The same name wins; else a table whose name
// ends with the asked name in whole words, as an extraction may put its file, sheet or client first ("Hamad_70714 Top
// holdings" is "Top holdings"). Never the tail of a word ("Xholdings" is not "Holdings").
export const tableNameKey = (value: string) => value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim()
export function findTable<T extends { name: string }>(name: string, tables: readonly T[]): T | undefined {
  const key = tableNameKey(name)
  if (!key) return undefined
  return tables.find((table) => tableNameKey(table.name) === key) ?? tables.find((table) => tableNameKey(table.name).endsWith(` ${key}`))
}

// "pe_coverage_pct" → "PE coverage %": a column key as words, with the usual finance abbreviations kept upper case.
const UPPER = new Set(["id", "pe", "nav", "ytd", "mtd", "qtd", "isin", "pnl", "fx", "usd", "qar", "aum", "cusip", "sedol"])
export function readableHeader(key: string) {
  const words = String(key || "").replace(/[_]+/g, " ").replace(/\s+/g, " ").trim().split(" ")
  if (!words[0]) return String(key || "")
  // Already readable ("POSN DATE", "Security"): leave it.
  if (!/_/.test(key) && /[A-Z ]/.test(key)) return key
  return words.map((word, index) => {
    const lower = word.toLowerCase()
    if (lower === "pct") return "%"
    if (UPPER.has(lower)) return lower.toUpperCase()
    return index === 0 ? lower[0].toUpperCase() + lower.slice(1) : lower
  }).join(" ")
}

// Column kinds for a table's cells (C9, T29): numbers right-aligned, grouped and rounded; yyyymmdd integers read as a
// day; ids and codes stay as they are written.
export type CellKind = "number" | "ymd" | "iso" | "code" | "text"
const CODE_HEADER = /(^|[\s_])(id|code|account|acct|portfolio|client|no|number|isin|cusip|sedol|ticker|year)($|[\s_])/i
const NUMBER = /^-?\d+(\.\d+)?(e[-+]?\d+)?$/i
const YMD = /^((?:19|20)\d{2})(\d{2})(\d{2})$/
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

export function columnKinds(headers: readonly string[], rows: ReadonlyArray<readonly string[]>): CellKind[] {
  return headers.map((header, column) => {
    const values = rows.map((row) => String(row[column] ?? "").trim()).filter((value) => value !== "" && value !== "-")
    if (!values.length) return "text"
    if (values.every((value) => ISO_DAY.test(value) && formatDay(value))) return "iso"
    if (values.every((value) => YMD.test(value) && ymdDay(value))) return /date|dt|as.?of|valuation|day/i.test(header) || values.length >= 3 ? "ymd" : "code"
    if (!values.every((value) => NUMBER.test(value))) return "text"
    return CODE_HEADER.test(header) && values.every((value) => /^-?\d+$/.test(value)) ? "code" : "number"
  })
}

// One cell's text: floats rounded (2 decimals from 1,000 up, else 4 significant digits), grouped; the raw value is the
// caller's tooltip.
export function formatCell(value: string, kind: CellKind, intl = "en-GB") {
  const text = String(value ?? "")
  if (kind === "ymd") return ymdDay(text.trim()) ?? text
  if (kind === "iso") return formatDay(text.trim()) || text
  if (kind !== "number" || !NUMBER.test(text.trim())) return text
  const number = Number(text)
  if (!Number.isFinite(number)) return text
  if (Number.isInteger(number)) return number.toLocaleString(intl)
  return Math.abs(number) >= 1000
    ? number.toLocaleString(intl, { maximumFractionDigits: 2, minimumFractionDigits: 2 })
    : number.toLocaleString(intl, { maximumSignificantDigits: 4 })
}

function ymdDay(value: string) {
  const match = YMD.exec(value)
  if (!match) return
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  if (date.getUTCMonth() !== Number(match[2]) - 1 || date.getUTCDate() !== Number(match[3])) return
  return formatDay(`${match[1]}-${match[2]}-${match[3]}`)
}

// This client's rows (T1, C3). A table carries a client key when a column named like Client / Portfolio / Account holds
// the client's code ("70714") or name ("Hamad_70714"); a validation names the client in its label or text.
export type ClientKey = { name: string; code?: string | null }
const CLIENT_HEADER = /client|portfolio|account|acct|customer|cif/i

export function clientCodes(client: ClientKey) {
  const name = client.name.trim()
  const suffix = /_(\w+)$/.exec(name)?.[1]
  return [...new Set([client.code?.trim(), name, suffix].filter((value): value is string => !!value))].map((value) => value.toLowerCase())
}

// The columns that hold this client's key, and the values that name the client there; no columns when none of the rows
// name it (the table is not client-keyed). A column holding the client's full name wins over one holding a bare code:
// two clients can share an account code ("Hamad_70714" and "zHamad_70714" both hold account 70714).
export function clientColumns(table: { headers: readonly string[]; rows: ReadonlyArray<Readonly<Record<string, string>>> }, client: ClientKey) {
  const holds = (values: string[]) => table.headers.filter((header) => CLIENT_HEADER.test(header) && table.rows.some((row) => values.includes(String(row[header] ?? "").trim().toLowerCase())))
  const name = [client.name.trim().toLowerCase()]
  const byName = holds(name)
  if (byName.length) return { columns: byName, codes: name }
  const codes = clientCodes(client)
  return { columns: holds(codes), codes }
}

export function clientRows<T extends Readonly<Record<string, string>>>(rows: readonly T[], key: { columns: readonly string[]; codes: readonly string[] }) {
  return rows.filter((row) => key.columns.some((column) => key.codes.includes(String(row[column] ?? "").trim().toLowerCase())))
}

// A validation (or any labelled item) about this client: its label or text names the client's code or name as a whole
// word ("zHamad_70714", a historical portfolio, is not Hamad_70714).
export function namesClient(text: string, client: ClientKey) {
  const value = text.toLowerCase()
  return clientCodes(client).some((code) => code.length > 2 && new RegExp(`(^|[^a-z0-9_])${code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}($|[^a-z0-9_])`).test(value))
}

// This client's items first, the rest in their order.
export function clientFirst<T>(items: readonly T[], text: (item: T) => string, client?: ClientKey) {
  if (!client) return [...items]
  return [...items.filter((item) => namesClient(text(item), client)), ...items.filter((item) => !namesClient(text(item), client))]
}
